import { getContentTypeResponse } from '@/lib/util';
import baseApi from '@/modules/services/base';
import {
  VideoLibraryTag,
  VocamRegionsTag,
  VocamChannelsTag,
  VideoPreviewLinkTag,
  VideoLinkTag,
} from '@/modules/services/tags';
import {
  CourseLibraryViewData,
  LibraryElearningsDataType,
  LibraryQueryInterface,
} from '@/pages/CourseLibrary/types';
import { PaginationType } from '../types/elearning';

interface VocamRegionType {
  createdAt: Date;
  modifiedAt: Date;
  regionId: number;
  region: string;
  culture: string;
}

interface VocamChannelType {
  createdAt: Date;
  modifiedAt: Date;
  channelId: number;
  name: string;
}

export interface VocamChapterType {
  id: number;
  name: string;
}
interface VocamModuleType extends VocamChapterType {
  description: string;
}

const videoLibraryApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getVideoLibraryElearnings: build.query<
      LibraryElearningsDataType,
      void | LibraryQueryInterface
    >({
      query: (params: LibraryQueryInterface) =>
        params
          ? {
              url: `elearning-library/videos`,
              params,
            }
          : { url: `elearning-library/videos` },
      providesTags: [VideoLibraryTag],
    }),
    getVideoLibraryElearningV2: build.query<
      PaginationType<CourseLibraryViewData>,
      void | LibraryQueryInterface
    >({
      query: (params: LibraryQueryInterface) =>
        params
          ? {
              url: `elearning-library/video-library`,
              params,
            }
          : { url: `elearning-library/video-library` },
      providesTags: [VideoLibraryTag],
    }),
    getVocamRegions: build.query<VocamRegionType[], void>({
      query: () => `vocam/regions`,
      providesTags: [VocamRegionsTag],
    }),
    getVocamChannels: build.query<VocamChannelType[], void>({
      query: () => `vocam/channels`,
      providesTags: [VocamChannelsTag],
    }),
    getVocamModules: build.query<
      VocamModuleType[],
      { channelId: number; regionId: number }
    >({
      query: (param) => `vocam/modules/${param.channelId}/${param.regionId}`,
      providesTags: [VocamChannelsTag],
    }),
    getVocamChapters: build.query<VocamChapterType[], number>({
      query: (moduleId) => `vocam/topics/${moduleId}`,
      providesTags: [VocamChannelsTag],
    }),
    getVideoPreviewLink: build.query<
      string,
      { moduleId: number; topicId: number }
    >({
      query: (param) => ({
        url: `vocam/getVideoPreviewLink/${param.moduleId}/${param.topicId}`,
        responseHandler: getContentTypeResponse,
      }),
      providesTags: [VideoPreviewLinkTag],
    }),
    getVideoLink: build.query<string, string>({
      query: (param) => ({
        url: `vocam/getVideoLink/${param}`,
        responseHandler: getContentTypeResponse,
        // Add data to cache with 5 minutes persistent
        keepUnusedDataFor: 300,
      }),
      providesTags: [VideoLinkTag],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetVideoLibraryElearningsQuery,
  useGetVideoLibraryElearningV2Query,
  useGetVocamRegionsQuery,
  useGetVocamChannelsQuery,
  useGetVocamModulesQuery,
  useGetVocamChaptersQuery,
  useGetVideoPreviewLinkQuery,
  useGetVideoLinkQuery,
} = videoLibraryApi;

export default videoLibraryApi;
